import { supabase } from '../lib/supabase'
import type { 
  Invoice, 
  CreateInvoiceData, 
  UpdateInvoiceData, 
  InvoiceFilters, 
  InvoiceStats,
  PlanLimits
} from '../types/invoice'
import { uploadedInvoiceService } from './uploadedInvoiceService'

class InvoiceService {
  // Plan limitations
  private readonly PLAN_LIMITS = {
    freemium: { maxInvoicesPerMonth: 5, hasUnlimitedInvoices: false },
    professional: { maxInvoicesPerMonth: -1, hasUnlimitedInvoices: true },
    studio: { maxInvoicesPerMonth: -1, hasUnlimitedInvoices: true }
  }

  private checkSupabase() {
    if (!supabase) {
      throw new Error('Supabase client not initialized')
    }
    return supabase
  }

  // Get plan limits for current user
  async getPlanLimits(userId: string, subscriptionStatus: string): Promise<PlanLimits> {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7) // YYYY-MM format
      
      // Get current month usage
      const { data: usage, error: usageError } = await this.checkSupabase()
        .from('invoice_usage_tracking')
        .select('invoices_created, invoices_sent')
        .eq('user_id', userId)
        .eq('month_year', currentMonth)
        .single()

      if (usageError && usageError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw usageError
      }

      const currentMonthUsage = usage?.invoices_created || 0
      const planType = subscriptionStatus as keyof typeof this.PLAN_LIMITS
      const limits = this.PLAN_LIMITS[planType] || this.PLAN_LIMITS.freemium

      // Get upload limits
      const uploadLimits = await uploadedInvoiceService.getUploadLimits(userId, subscriptionStatus)

      return {
        maxInvoicesPerMonth: limits.maxInvoicesPerMonth,
        hasUnlimitedInvoices: limits.hasUnlimitedInvoices,
        currentMonthUsage,
        canCreateInvoice: limits.hasUnlimitedInvoices || currentMonthUsage < limits.maxInvoicesPerMonth,
        canSendInvoice: true, // All plans can send invoices they've created
        maxUploadedInvoices: uploadLimits.maxUploadedInvoices,
        hasUnlimitedUploads: uploadLimits.hasUnlimitedUploads,
        currentUploadedCount: uploadLimits.currentUploadedCount,
        canUploadInvoice: uploadLimits.canUploadInvoice
      }
    } catch (error) {
      console.error('Error getting plan limits:', error)
      // Return permissive defaults on error to allow invoice creation
      const planType = subscriptionStatus as keyof typeof this.PLAN_LIMITS
      const limits = this.PLAN_LIMITS[planType] || this.PLAN_LIMITS.freemium
      
      return {
        maxInvoicesPerMonth: limits.maxInvoicesPerMonth,
        hasUnlimitedInvoices: limits.hasUnlimitedInvoices,
        currentMonthUsage: 0, // Assume 0 usage if we can't fetch data
        canCreateInvoice: true, // Allow creation on error
        canSendInvoice: true,
        maxUploadedInvoices: planType === 'freemium' ? 5 : -1,
        hasUnlimitedUploads: planType !== 'freemium',
        currentUploadedCount: 0,
        canUploadInvoice: true
      }
    }
  }

  // Generate next invoice number
  async generateInvoiceNumber(userId: string): Promise<string> {
    try {
      const { data, error } = await this.checkSupabase()
        .rpc('generate_invoice_number', { user_uuid: userId })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error generating invoice number:', error)
      // Fallback to timestamp-based number
      const timestamp = Date.now().toString().slice(-6)
      return `INV-${timestamp}`
    }
  }

  // Create a new invoice
  async createInvoice(userId: string, data: CreateInvoiceData): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    try {
      // Check plan limits first
      const { data: profile } = await this.checkSupabase()
        .from('profiles')
        .select('subscription_status')
        .eq('id', userId)
        .single()

      const planLimits = await this.getPlanLimits(userId, profile?.subscription_status || 'freemium')
      
      if (!planLimits.canCreateInvoice) {
        return {
          success: false,
          error: `You've reached your monthly limit of ${planLimits.maxInvoicesPerMonth} invoices. Upgrade to Professional for unlimited invoices.`
        }
      }

      // Generate invoice number
      const invoiceNumber = await this.generateInvoiceNumber(userId)

      // Calculate totals
      const subtotal = data.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0)
      const discountAmount = data.discountAmount || 0
      const taxAmount = (subtotal - discountAmount) * ((data.taxRate || 0) / 100)
      const totalAmount = subtotal - discountAmount + taxAmount

      // Create invoice
      const { data: invoice, error: invoiceError } = await this.checkSupabase()
        .from('invoices')
        .insert({
          invoice_number: invoiceNumber,
          client_id: data.clientId,
          user_id: userId,
          title: data.title,
          description: data.description,
          due_date: data.dueDate,
          subtotal,
          tax_rate: data.taxRate || 0,
          tax_amount: taxAmount,
          discount_amount: discountAmount,
          total_amount: totalAmount,
          notes: data.notes,
          terms: data.terms,
          footer_text: data.footerText
        })
        .select()
        .single()

      if (invoiceError) throw invoiceError

      // Create invoice items
      const invoiceItems = data.items.map((item, index) => ({
        invoice_id: invoice.id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.quantity * item.unitPrice,
        item_type: item.itemType || 'service',
        notes: item.notes,
        sort_order: index
      }))

      const { error: itemsError } = await this.checkSupabase()
        .from('invoice_items')
        .insert(invoiceItems)

      if (itemsError) throw itemsError

      // Get the complete invoice with client and items
      const completeInvoice = await this.getInvoiceById(invoice.id)
      
      return {
        success: true,
        invoice: completeInvoice.invoice
      }
    } catch (error) {
      console.error('Error creating invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create invoice'
      }
    }
  }

  // Get invoice by ID
  async getInvoiceById(invoiceId: string): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    try {
      const { data: invoice, error: invoiceError } = await this.checkSupabase()
        .from('invoices')
        .select(`
          *,
          client:clients(id, name, email, company),
          items:invoice_items(*)
        `)
        .eq('id', invoiceId)
        .single()

      if (invoiceError) throw invoiceError

      return {
        success: true,
        invoice: this.transformInvoiceFromDB(invoice)
      }
    } catch (error) {
      console.error('Error getting invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get invoice'
      }
    }
  }

  // Get all invoices for user
  async getInvoices(userId: string, filters?: InvoiceFilters): Promise<{ success: boolean; invoices?: Invoice[]; error?: string }> {
    try {
      let query = this.checkSupabase()
        .from('invoices')
        .select(`
          *,
          client:clients(id, name, email, company)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      // Apply filters
      if (filters?.status?.length) {
        query = query.in('status', filters.status)
      }
      if (filters?.paymentStatus?.length) {
        query = query.in('payment_status', filters.paymentStatus)
      }
      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId)
      }
      if (filters?.dateFrom) {
        query = query.gte('issue_date', filters.dateFrom)
      }
      if (filters?.dateTo) {
        query = query.lte('issue_date', filters.dateTo)
      }
      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,invoice_number.ilike.%${filters.search}%`)
      }

      const { data: invoices, error } = await query

      if (error) throw error

      return {
        success: true,
        invoices: invoices.map(invoice => this.transformInvoiceFromDB(invoice))
      }
    } catch (error) {
      console.error('Error getting invoices:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get invoices'
      }
    }
  }

  // Update invoice
  async updateInvoice(invoiceId: string, data: UpdateInvoiceData): Promise<{ success: boolean; invoice?: Invoice; error?: string }> {
    try {
      const updateData: any = {}
      
      // Map camelCase to snake_case
      if (data.title !== undefined) updateData.title = data.title
      if (data.description !== undefined) updateData.description = data.description
      if (data.dueDate !== undefined) updateData.due_date = data.dueDate
      if (data.taxRate !== undefined) updateData.tax_rate = data.taxRate
      if (data.discountAmount !== undefined) updateData.discount_amount = data.discountAmount
      if (data.notes !== undefined) updateData.notes = data.notes
      if (data.terms !== undefined) updateData.terms = data.terms
      if (data.footerText !== undefined) updateData.footer_text = data.footerText
      if (data.status !== undefined) updateData.status = data.status
      if (data.paymentStatus !== undefined) updateData.payment_status = data.paymentStatus
      if (data.paymentDate !== undefined) updateData.payment_date = data.paymentDate
      if (data.paymentMethod !== undefined) updateData.payment_method = data.paymentMethod
      if (data.paymentNotes !== undefined) updateData.payment_notes = data.paymentNotes

      // Set sent_at timestamp when status changes to 'sent'
      if (data.status === 'sent') {
        updateData.sent_at = new Date().toISOString()
      }

      const { error } = await this.checkSupabase()
        .from('invoices')
        .update(updateData)
        .eq('id', invoiceId)
        .select()
        .single()

      if (error) throw error

      // Get the complete invoice
      const completeInvoice = await this.getInvoiceById(invoiceId)
      
      return {
        success: true,
        invoice: completeInvoice.invoice
      }
    } catch (error) {
      console.error('Error updating invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update invoice'
      }
    }
  }

  // Delete invoice
  async deleteInvoice(invoiceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // First, get the invoice details to know which user and month to decrement
      const { data: invoice, error: fetchError } = await this.checkSupabase()
        .from('invoices')
        .select('user_id, created_at')
        .eq('id', invoiceId)
        .single()

      if (fetchError) throw fetchError

      // Delete the invoice
      const { error: deleteError } = await this.checkSupabase()
        .from('invoices')
        .delete()
        .eq('id', invoiceId)

      if (deleteError) throw deleteError

      // Decrement the usage tracking count
      if (invoice) {
        const createdMonth = new Date(invoice.created_at).toISOString().slice(0, 7) // YYYY-MM format
        
        // Try to decrement the usage count
        const { error: usageError } = await this.checkSupabase()
          .rpc('decrement_invoice_usage', {
            user_uuid: invoice.user_id,
            target_month: createdMonth
          })

        // If the RPC function doesn't exist, handle it manually
        if (usageError && usageError.message?.includes('function')) {
          // Fallback: manually decrement the usage
          const { data: currentUsage } = await this.checkSupabase()
            .from('invoice_usage_tracking')
            .select('invoices_created')
            .eq('user_id', invoice.user_id)
            .eq('month_year', createdMonth)
            .single()

          if (currentUsage && currentUsage.invoices_created > 0) {
            await this.checkSupabase()
              .from('invoice_usage_tracking')
              .update({ 
                invoices_created: currentUsage.invoices_created - 1,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', invoice.user_id)
              .eq('month_year', createdMonth)
          }
        }
      }

      return { success: true }
    } catch (error) {
      console.error('Error deleting invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete invoice'
      }
    }
  }

  // Get monthly income for current month
  async getMonthlyIncome(userId: string): Promise<{ success: boolean; income?: number; error?: string }> {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7) // YYYY-MM format
      console.log('Getting monthly income for user:', userId, 'month:', currentMonth)
      
      // Get regular invoices for current month
      const { data: regularInvoices, error: regularError } = await this.checkSupabase()
        .from('invoices')
        .select('total_amount, payment_date, created_at')
        .eq('user_id', userId)
        .eq('payment_status', 'paid')

      if (regularError) {
        console.error('Error fetching regular invoices:', regularError)
        throw regularError
      }

      // Get uploaded invoices for current month
      const { data: uploadedInvoices, error: uploadedError } = await this.checkSupabase()
        .from('uploaded_invoices')
        .select('amount, paid_amount, paid_date, uploaded_at, payment_status')
        .eq('user_id', userId)
        .eq('payment_status', 'paid')

      if (uploadedError) {
        console.error('Error fetching uploaded invoices:', uploadedError)
        throw uploadedError
      }

      console.log('Regular invoices found:', regularInvoices)
      console.log('Uploaded invoices found:', uploadedInvoices)

      // Filter regular invoices by current month
      const currentMonthRegularInvoices = regularInvoices.filter(invoice => {
        const dateToCheck = invoice.payment_date || invoice.created_at
        return dateToCheck && dateToCheck.startsWith(currentMonth)
      })

      // Filter uploaded invoices by current month
      const currentMonthUploadedInvoices = uploadedInvoices.filter(invoice => {
        // For uploaded invoices, use paid_date if available, otherwise uploaded_at
        const dateToCheck = invoice.paid_date || invoice.uploaded_at
        return dateToCheck && dateToCheck.startsWith(currentMonth)
      })

      console.log('Current month regular invoices:', currentMonthRegularInvoices)
      console.log('Current month uploaded invoices:', currentMonthUploadedInvoices)

      // Calculate total income from regular invoices
      const regularIncome = currentMonthRegularInvoices.reduce((sum, invoice) => {
        return sum + parseFloat(invoice.total_amount.toString())
      }, 0)

      // Calculate total income from uploaded invoices
      const uploadedIncome = currentMonthUploadedInvoices.reduce((sum, invoice) => {
        // Use paid_amount if available, otherwise use amount
        const amount = invoice.paid_amount || invoice.amount || 0
        return sum + parseFloat(amount.toString())
      }, 0)

      const totalIncome = regularIncome + uploadedIncome

      console.log('Regular income:', regularIncome)
      console.log('Uploaded income:', uploadedIncome)
      console.log('Total monthly income:', totalIncome)

      return {
        success: true,
        income: totalIncome
      }
    } catch (error) {
      console.error('Error getting monthly income:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get monthly income'
      }
    }
  }

  // Get invoice statistics including uploaded invoices
  async getInvoiceStats(userId: string): Promise<{
    success: boolean
    stats?: InvoiceStats
    error?: string
  }> {
    try {
      // Get regular invoices stats - FIXED: using correct snake_case column names
      const { data: invoices, error: invoicesError } = await this.checkSupabase()
        .from('invoices')
        .select('total_amount, status, created_at, payment_status')
        .eq('user_id', userId)

      if (invoicesError) throw invoicesError

      // Get uploaded invoices stats
      const { data: uploadedInvoices, error: uploadedError } = await this.checkSupabase()
        .from('uploaded_invoices')
        .select('amount, status, uploaded_at, payment_status, paid_amount, paid_date')
        .eq('user_id', userId)

      if (uploadedError) throw uploadedError

      const now = new Date()
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()

      // Calculate regular invoices stats
      let totalInvoices = invoices?.length || 0
      let totalAmount = 0
      let paidAmount = 0
      let overdueAmount = 0
      let thisMonthInvoices = 0
      let thisMonthAmount = 0

      invoices?.forEach(invoice => {
        const amount = parseFloat(invoice.total_amount?.toString() || '0')
        totalAmount += amount

        if (invoice.payment_status === 'paid') {
          paidAmount += amount
        } else if (invoice.status === 'overdue') {
          overdueAmount += amount
        }

        const invoiceDate = new Date(invoice.created_at)
        if (invoiceDate.getMonth() === currentMonth && invoiceDate.getFullYear() === currentYear) {
          thisMonthInvoices++
          thisMonthAmount += amount
        }
      })

      // Add uploaded invoices to stats
      uploadedInvoices?.forEach(invoice => {
        const amount = parseFloat(invoice.amount?.toString() || '0')
        const paidAmt = parseFloat(invoice.paid_amount?.toString() || '0')
        
        if (amount > 0) {
          totalInvoices++
          totalAmount += amount

          if (invoice.payment_status === 'paid') {
            paidAmount += paidAmt || amount
          } else if (invoice.status === 'overdue') {
            overdueAmount += amount
          }

          // For thisMonthAmount, use the payment date if paid, otherwise use upload date
          let dateToCheck: Date
          if (invoice.payment_status === 'paid' && invoice.paid_date) {
            // If it's paid and has a paid_date, use that for "this month" calculation
            dateToCheck = new Date(invoice.paid_date)
          } else {
            // Otherwise use upload date
            dateToCheck = new Date(invoice.uploaded_at)
          }

          if (dateToCheck.getMonth() === currentMonth && dateToCheck.getFullYear() === currentYear) {
            thisMonthInvoices++
            // For this month amount, only count paid invoices with their paid amount
            if (invoice.payment_status === 'paid') {
              thisMonthAmount += paidAmt || amount
            } else {
              // For unpaid invoices uploaded this month, count the full amount
              thisMonthAmount += amount
            }
          }
        }
      })

      const stats: InvoiceStats = {
        totalInvoices,
        totalAmount,
        paidAmount,
        unpaidAmount: totalAmount - paidAmount,
        overdueAmount,
        thisMonthInvoices,
        thisMonthAmount
      }

      return { success: true, stats }
    } catch (error) {
      console.error('Error getting invoice stats:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get invoice statistics'
      }
    }
  }

  // Update invoice items
  async updateInvoiceItems(invoiceId: string, items: Array<{ id?: string; description: string; quantity: number; unitPrice: number; itemType?: string; notes?: string }>): Promise<{ success: boolean; error?: string }> {
    try {
      // Delete existing items
      await this.checkSupabase()
        .from('invoice_items')
        .delete()
        .eq('invoice_id', invoiceId)

      // Insert new items
      const invoiceItems = items.map((item, index) => ({
        invoice_id: invoiceId,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.quantity * item.unitPrice,
        item_type: item.itemType || 'service',
        notes: item.notes,
        sort_order: index
      }))

      const { error } = await this.checkSupabase()
        .from('invoice_items')
        .insert(invoiceItems)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error updating invoice items:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update invoice items'
      }
    }
  }

  // Mark invoice as viewed (for tracking)
  async markInvoiceAsViewed(invoiceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.checkSupabase()
        .from('invoices')
        .update({ 
          status: 'viewed',
          viewed_at: new Date().toISOString()
        })
        .eq('id', invoiceId)
        .eq('status', 'sent') // Only update if currently 'sent'

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error marking invoice as viewed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to mark invoice as viewed'
      }
    }
  }

  // Transform database invoice to frontend format
  private transformInvoiceFromDB(dbInvoice: any): Invoice {
    return {
      id: dbInvoice.id,
      invoiceNumber: dbInvoice.invoice_number,
      clientId: dbInvoice.client_id,
      userId: dbInvoice.user_id,
      title: dbInvoice.title,
      description: dbInvoice.description,
      issueDate: dbInvoice.issue_date,
      dueDate: dbInvoice.due_date,
      subtotal: parseFloat(dbInvoice.subtotal.toString()),
      taxRate: parseFloat(dbInvoice.tax_rate.toString()),
      taxAmount: parseFloat(dbInvoice.tax_amount.toString()),
      discountAmount: parseFloat(dbInvoice.discount_amount.toString()),
      totalAmount: parseFloat(dbInvoice.total_amount.toString()),
      status: dbInvoice.status,
      paymentStatus: dbInvoice.payment_status,
      paymentDate: dbInvoice.payment_date,
      paymentMethod: dbInvoice.payment_method,
      paymentNotes: dbInvoice.payment_notes,
      notes: dbInvoice.notes,
      terms: dbInvoice.terms,
      footerText: dbInvoice.footer_text,
      createdAt: dbInvoice.created_at,
      updatedAt: dbInvoice.updated_at,
      sentAt: dbInvoice.sent_at,
      viewedAt: dbInvoice.viewed_at,
      client: dbInvoice.client ? {
        id: dbInvoice.client.id,
        name: dbInvoice.client.name,
        email: dbInvoice.client.email,
        company: dbInvoice.client.company
      } : undefined,
      items: dbInvoice.items ? dbInvoice.items.map((item: any) => ({
        id: item.id,
        invoiceId: item.invoice_id,
        description: item.description,
        quantity: parseFloat(item.quantity.toString()),
        unitPrice: parseFloat(item.unit_price.toString()),
        totalPrice: parseFloat(item.total_price.toString()),
        itemType: item.item_type,
        notes: item.notes,
        sortOrder: item.sort_order,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      })) : undefined
    }
  }

  // Generate and download all invoices as a zip file
  async downloadAllInvoicesAsZip(userId: string, userProfile?: any): Promise<{ success: boolean; error?: string }> {
    try {
      // Dynamic import for JSZip to reduce bundle size
      const JSZip = (await import('jszip')).default
      const { generateInvoicePDF } = await import('../utils/pdfGenerator')
      
      // Get all invoices for the user
      const result = await this.getInvoices(userId)
      if (!result.success || !result.invoices) {
        return { success: false, error: 'Failed to fetch invoices' }
      }

      const invoices = result.invoices
      if (invoices.length === 0) {
        return { success: false, error: 'No invoices found to download' }
      }

      const zip = new JSZip()
      const currentYear = new Date().getFullYear()
      
      // Create folders by year
      const invoicesByYear: { [year: string]: Invoice[] } = {}
      
      invoices.forEach(invoice => {
        const year = new Date(invoice.issueDate).getFullYear().toString()
        if (!invoicesByYear[year]) {
          invoicesByYear[year] = []
        }
        invoicesByYear[year].push(invoice)
      })

      // Generate PDFs and add to zip
      for (const [year, yearInvoices] of Object.entries(invoicesByYear)) {
        const yearFolder = zip.folder(year)
        
        for (const invoice of yearInvoices) {
          try {
            // Get full invoice details including items
            const fullInvoiceResult = await this.getInvoiceById(invoice.id)
            if (fullInvoiceResult.success && fullInvoiceResult.invoice) {
              const pdfBlob = generateInvoicePDF(fullInvoiceResult.invoice, userProfile)
              const pdfBuffer = await pdfBlob.arrayBuffer()
              
              // Clean filename for file system compatibility
              const cleanClientName = invoice.client?.name?.replace(/[^a-zA-Z0-9\s-]/g, '') || 'Unknown'
              const filename = `${invoice.invoiceNumber}_${cleanClientName}.pdf`
              
              yearFolder?.file(filename, pdfBuffer)
            }
          } catch (error) {
            console.error(`Error generating PDF for invoice ${invoice.invoiceNumber}:`, error)
            // Continue with other invoices even if one fails
          }
        }
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      
      // Create download link
      const url = URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `ShootSuite_Invoices_${currentYear}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      return { success: true }
    } catch (error) {
      console.error('Error creating invoice zip:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create zip file' 
      }
    }
  }
}

export const invoiceService = new InvoiceService() 